/**
 * Vivi Filter (vf) — "pill" dropdown filtri.
 *
 * Markup: functions/vivi-filter.php (vf_filter_open / vf_filter_group_open …)
 * Stili:  src/styles/_vivi-filter.scss
 *
 * Inicializira vsak koren `.vf[data-vf]` (več instanc na strani je OK). Skrbi za:
 *  - odpiranje/zapiranje skupin (klik/tap na pill, gumb X, klik izven, Escape, Tab ven)
 *    `data-vf-single` na korenu → odprta samo ena skupina naenkrat
 *  - aria-expanded + razred .is-open na .vf-group (stanje za CSS)
 *  - na mobilnem (panel v toku) po odprtju gladko pripelje panel v vidno polje
 *  - značko s številom aktivnih izbir (.vf-count); vrednost `data-vf-neutral` na skupini
 *    (npr. 'unrated', 'all') se ne šteje
 *  - ponovni klik/tap na izbran radio v skupini z `data-vf-neutral` se vrne na nevtralno
 *    vrednost in sproži 'change' (da se seznam ponovno naloži)
 *
 * Ločeno od filter-expander.js / intersection-animations.js (sidebar filtri ostanejo nedotaknjeni).
 */

const MOBILE_MQ = '(max-width: 63.99rem)' // < Tailwind lg
const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)'

const instances = []

class ViviFilter {
	constructor(root) {
		this.root = root
		this.single = root.hasAttribute('data-vf-single')
		this.groups = Array.from(root.querySelectorAll('.vf-group'))
		if (!this.groups.length) return

		this.groups.forEach((group) => this.bindGroup(group))
		this.bindRoot()
		this.updateAllCounts()
		// Po tem, ko drugi moduli nastavijo vrednosti iz piškotka
		setTimeout(() => this.updateAllCounts(), 300)

		instances.push(this)
	}

	/* ---------- odpiranje / zapiranje ---------- */

	isOpen(group) {
		return group.classList.contains('is-open')
	}

	setOpen(group, open) {
		if (this.isOpen(group) === open) return
		const toggle = group.querySelector('.vf-toggle')
		group.classList.toggle('is-open', open)
		toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
		if (open) this.revealOnMobile(group)
	}

	closeAll(except = null) {
		this.groups.forEach((g) => {
			if (g !== except) this.setOpen(g, false)
		})
	}

	openGroup() {
		return this.groups.find((g) => this.isOpen(g)) || null
	}

	// Na mobilnem je panel v toku pod pilli — po animaciji ga pripeljemo v vidno polje
	revealOnMobile(group) {
		if (!window.matchMedia(MOBILE_MQ).matches) return
		const panel = group.querySelector('.vf-panel')
		if (!panel) return

		let done = false
		const reveal = () => {
			if (done || !this.isOpen(group)) return
			done = true
			const rect = panel.getBoundingClientRect()
			const vh = window.innerHeight || document.documentElement.clientHeight
			if (rect.bottom <= vh && rect.top >= 0) return
			panel.scrollIntoView({
				block: 'nearest',
				behavior: window.matchMedia(REDUCED_MOTION_MQ).matches ? 'auto' : 'smooth',
			})
		}

		panel.addEventListener('transitionend', function onEnd(e) {
			if (e.propertyName !== 'grid-template-rows') return
			panel.removeEventListener('transitionend', onEnd)
			reveal()
		})
		setTimeout(reveal, 400) // rezerva, če transitionend ne pride (reduced motion ipd.)
	}

	/* ---------- skupina ---------- */

	bindGroup(group) {
		const toggle = group.querySelector('.vf-toggle')
		const close = group.querySelector('.vf-close')

		toggle.addEventListener('click', () => {
			const willOpen = !this.isOpen(group)
			if (this.single) this.closeAll(group)
			this.setOpen(group, willOpen)
		})

		if (close) {
			close.addEventListener('click', () => {
				this.setOpen(group, false)
				toggle.focus({ preventScroll: true })
			})
		}

		this.bindNeutralReset(group)
	}

	// Ponovni klik/tap na izbran radio → nazaj na nevtralno vrednost (data-vf-neutral)
	bindNeutralReset(group) {
		const neutral = group.dataset.vfNeutral
		if (!neutral) return

		const neutralInput = group.querySelector(`input[type=radio][value="${CSS.escape(neutral)}"]`)
		if (!neutralInput) return

		group.querySelectorAll('label.vf-item').forEach((label) => {
			const input = label.querySelector('input[type=radio]')
			if (!input || input === neutralInput) return

			let wasChecked = false
			// pointerdown (prst ali miška) se zgodi pred sintetičnim klikom labele
			label.addEventListener('pointerdown', () => { wasChecked = input.checked })
			label.addEventListener('keydown', (e) => {
				if (e.key === ' ' || e.key === 'Enter') wasChecked = input.checked
			})

			input.addEventListener('click', () => {
				if (!wasChecked) return
				wasChecked = false
				neutralInput.checked = true
				neutralInput.dispatchEvent(new Event('change', { bubbles: true }))
			})
		})
	}

	/* ---------- značke ---------- */

	updateCount(group) {
		const badge = group.querySelector('.vf-count')
		if (!badge) return

		const neutral = group.dataset.vfNeutral
		const checked = Array.from(group.querySelectorAll('input:checked'))
			.filter((input) => input.value !== neutral)

		badge.textContent = checked.length
		badge.classList.toggle('hidden', checked.length === 0)
		badge.classList.toggle('inline-flex', checked.length > 0)
		group.classList.toggle('has-active', checked.length > 0)
	}

	updateAllCounts() {
		this.groups.forEach((g) => this.updateCount(g))
	}

	/* ---------- koren ---------- */

	bindRoot() {
		this.root.addEventListener('change', (e) => {
			const group = e.target.closest('.vf-group')
			if (group) this.updateCount(group)
		})

		// Fokus s tipkovnico izven komponente (Tab) zapre odprto skupino
		this.root.addEventListener('focusout', (e) => {
			if (e.relatedTarget && !this.root.contains(e.relatedTarget)) this.closeAll()
		})

		// Zunanja osvežitev (npr. po programski nastavitvi inputov): $(root).trigger('vf:refresh')
		this.root.addEventListener('vf:refresh', () => this.updateAllCounts())
	}
}

/* ---------- globalno ---------- */

let globalBound = false
const bindGlobal = () => {
	if (globalBound) return
	globalBound = true

	// Klik/tap izven zapre vse
	document.addEventListener('pointerdown', (e) => {
		instances.forEach((inst) => {
			if (!inst.root.contains(e.target)) inst.closeAll()
		})
	})

	// Escape zapre in vrne fokus na pill
	document.addEventListener('keydown', (e) => {
		if (e.key !== 'Escape') return
		instances.forEach((inst) => {
			const open = inst.openGroup()
			if (!open) return
			inst.setOpen(open, false)
			open.querySelector('.vf-toggle').focus({ preventScroll: true })
		})
	})
}

export default {
	init(root = document) {
		root.querySelectorAll('.vf[data-vf]').forEach((el) => {
			if (!el.viviFilter) el.viviFilter = new ViviFilter(el)
		})
		bindGlobal()
	}
}
